import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { AssetPublic, AdSpacePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Trash2 } from "lucide-react";
import { ListBackButton } from "@/components/list-back-button";

export default function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [asset, setAsset] = useState<(AssetPublic & { ad_spaces?: AdSpacePublic[] }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .getAsset(id)
      .then((res) => setAsset(res.asset))
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleDeleteAdSpace(adSpaceId: string) {
    if (!confirm("Delete this ad space?")) return;
    try {
      await api.deleteAdSpace(adSpaceId);
      setAsset((prev) =>
        prev
          ? { ...prev, ad_spaces: prev.ad_spaces?.filter((s) => s.id !== adSpaceId) }
          : prev,
      );
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete");
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="asset-detail" data-state="loading">Loading...</p>;
  if (error) return <p className="text-sm text-destructive" data-page="asset-detail" data-state="error" data-error>{error}</p>;
  if (!asset) return <p className="text-muted-foreground" data-page="asset-detail" data-state="empty">Asset not found</p>;

  return (
    <div className="flex flex-col gap-6" data-page="asset-detail" data-state="ready">
      <div className="flex items-center gap-3" data-region="header">
        <ListBackButton to="/courts" label="Courts" />
        <div>
          <h1 className="text-2xl font-semibold">{asset.name ?? "Unnamed Asset"}</h1>
          <p className="text-muted-foreground">
            {asset.asset_type_name} -- {asset.court_name}
          </p>
          {asset.position_zone && (
            <p className="text-sm text-muted-foreground">Zone: {asset.position_zone}</p>
          )}
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-lg font-medium">Ad Spaces ({asset.ad_space_count})</h2>
        {(!asset.ad_spaces || asset.ad_spaces.length === 0) ? (
          <p className="text-muted-foreground">No ad spaces</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Size (mm)</TableHead>
                  <TableHead>Placements</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {asset.ad_spaces.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {s.width_mm} x {s.height_mm}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {s.placement_count}
                    </TableCell>
                    <TableCell>
                      {asset._actions.can_delete && (
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteAdSpace(s.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
